import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, X, RotateCcw, Check, Loader2, AlertCircle, Zap, Edit2, Plus, Trash2, Search, MapPin, Clock, ChevronRight, Share2, Sparkles, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = window.location.origin + '/api';
const _getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export default function ScorecardScanner({ onScanComplete, onClose, startMode = 'scan', currentUser = null }) {
  // startMode: 'scan' (default, opens camera flow) | 'manual' (skips camera, goes straight to confirm view for manual hole-by-hole entry)
  const isManualMode = startMode === 'manual';
  const [mode, setMode] = useState('course'); // course, camera, processing, confirm, action
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseSearch, setCourseSearch] = useState('');
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [scannedData, setScannedData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Multi-group support
  const [allScannedGroups, setAllScannedGroups] = useState([]); // Array of scanned group data
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);

  // CTP (Closest To Pin) — { [holeNum]: globalId }
  const [ctpAssignments, setCtpAssignments] = useState({});

  // Where to return after FillCourseDetails: 'camera' (default — pre-scan) or 'confirm' (post-scan)
  const [fillReturnMode, setFillReturnMode] = useState('camera');
  
  // Member autocomplete
  const [members, setMembers] = useState([]);
  const [activeMemberDropdown, setActiveMemberDropdown] = useState(null); // playerIndex with open dropdown
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const nativeCameraRef = useRef(null);

  // Member picker (inline "Add Bundle Member" flow — manual mode)
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [memberPickerQuery, setMemberPickerQuery] = useState('');

  // Fetch members list on mount
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const res = await fetch(`${API}/members/directory`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          // Response is direct array
          setMembers(Array.isArray(data) ? data : data.members || []);
        }
      } catch (err) {
        console.error('Failed to fetch members:', err);
      }
    };
    fetchMembers();
  }, []);

  // Filter members based on search
  const filteredMembers = React.useMemo(() => {
    if (!memberSearchQuery) return members.slice(0, 10);
    const query = memberSearchQuery.toLowerCase();
    return members
      .filter(m => m.name?.toLowerCase().includes(query))
      .slice(0, 10);
  }, [members, memberSearchQuery]);

  // Auto-match OCR names to members
  const matchNameToMember = useCallback((ocrName) => {
    if (!ocrName || !members.length) return null;
    const nameLower = ocrName.toLowerCase().trim();
    
    // Exact match
    const exact = members.find(m => m.name?.toLowerCase() === nameLower);
    if (exact) return exact;
    
    // Partial match (first name or last name)
    const partial = members.find(m => {
      const memberName = m.name?.toLowerCase() || '';
      const nameParts = memberName.split(' ');
      return nameParts.some(part => part === nameLower || nameLower.includes(part));
    });
    if (partial) return partial;
    
    // Fuzzy match (contains)
    const fuzzy = members.find(m => 
      m.name?.toLowerCase().includes(nameLower) || nameLower.includes(m.name?.toLowerCase() || '')
    );
    return fuzzy || null;
  }, [members]);

  // Merge all players from all groups
  const allPlayers = React.useMemo(() => {
    const players = [];
    allScannedGroups.forEach((group, groupIdx) => {
      group.players?.forEach((player, playerIdx) => {
        players.push({
          ...player,
          groupIndex: groupIdx,
          groupName: `Group ${groupIdx + 1}`,
          originalIndex: playerIdx
        });
      });
    });
    // Also add current scannedData if not yet added to groups
    if (scannedData?.players && !allScannedGroups.includes(scannedData)) {
      scannedData.players.forEach((player, playerIdx) => {
        players.push({
          ...player,
          groupIndex: allScannedGroups.length,
          groupName: `Group ${allScannedGroups.length + 1}`,
          originalIndex: playerIdx
        });
      });
    }
    return players;
  }, [allScannedGroups, scannedData]);

  // Search courses
  const searchCourses = useCallback(async (query) => {
    setLoadingCourses(true);
    try {
      const res = await fetch(`${API}/social/courses/search?q=${encodeURIComponent(query)}`, {
        credentials: 'include',
        headers: _getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCourses(data.courses || []);
      }
    } catch (err) {
      console.error('Course search error:', err);
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  // Load initial courses
  useEffect(() => {
    searchCourses('');
  }, [searchCourses]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (courseSearch) {
        searchCourses(courseSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [courseSearch, searchCourses]);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Unable to access camera. Please use file upload instead.');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Capture photo
  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      setError('Camera not ready — try HD Cam or Gallery instead');
      return;
    }
    
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError('Camera not ready yet — wait a moment and try again');
      return;
    }
    
    const canvas = canvasRef.current;
    
    // Cap at 1280px for faster upload while still good OCR quality
    const maxDim = 1280;
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (w > maxDim || h > maxDim) {
      if (w > h) { h = Math.round((h / w) * maxDim); w = maxDim; }
      else { w = Math.round((w / h) * maxDim); h = maxDim; }
    }
    canvas.width = w;
    canvas.height = h;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, w, h);
    
    // Export at near-max quality — don't re-compress with enhanceImage for camera
    // (aggressive grayscale + high-contrast was destroying handwriting detail)
    const imageData = canvas.toDataURL('image/jpeg', 0.95);
    
    if (!imageData || imageData.length < 1000) {
      setError('Failed to capture image — try HD Cam or Gallery instead');
      return;
    }
    
    setCapturedImage(imageData);
    stopCamera();
    
    // Send the raw capture straight to GPT-4o — skip the enhanceImage double-compression
    processImage(imageData);
  }, [stopCamera]);

  // Handle file upload (from gallery)
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or HEIC image');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setCapturedImage(event.target.result);
      // Gallery images are already well-processed by the phone camera app — skip enhanceImage
      processImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle native camera capture (triggers phone's built-in camera/doc scanner)
  const handleNativeCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setCapturedImage(event.target.result);
      // Native phone camera already produces well-processed images — skip enhanceImage
      processImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Enhanced image processing for better OCR
  // Lightweight downscaler — keeps OCR quality but caps file size to ~1.5MB
  // so mobile camera photos (5-10MB) don't time out the Vision API
  const downscaleIfLarge = (imageDataUrl) => {
    return new Promise((resolve) => {
      // Rough size check: base64 length * 0.75 ≈ bytes
      const approxBytes = imageDataUrl.length * 0.75;
      if (approxBytes < 1_500_000) { resolve(imageDataUrl); return; }
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const maxSize = 2048;
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) { height = Math.round((height / width) * maxSize); width = maxSize; }
          else { width = Math.round((width / height) * maxSize); height = maxSize; }
        }
        canvas.width = width; canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => resolve(imageDataUrl);
      img.src = imageDataUrl;
    });
  };

  const enhanceImage = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Use full resolution for best OCR accuracy
        const maxSize = 2048;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height / width) * maxSize);
            width = maxSize;
          } else {
            width = Math.round((width / height) * maxSize);
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Sharpen + high contrast for handwritten numbers
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        
        // Increase contrast aggressively for handwriting
        for (let i = 0; i < data.length; i += 4) {
          // Convert to grayscale
          const gray = (data[i] * 0.299 + data[i+1] * 0.587 + data[i+2] * 0.114);
          // High contrast: push dark darker, light lighter
          const contrast = 1.5;
          const val = Math.min(255, Math.max(0, ((gray - 128) * contrast) + 128));
          data[i] = val;
          data[i+1] = val;
          data[i+2] = val;
        }
        
        ctx.putImageData(imageData, 0, 0);
        
        // Return high quality JPEG
        resolve(canvas.toDataURL('image/jpeg', 0.95));
      };
      img.onerror = () => resolve(imageDataUrl);
      img.src = imageDataUrl;
    });
  };

  // Process image with OCR — auto-retries once on transient backend failures
  const processImage = async (imageDataUrl, attempt = 1) => {
    setMode('processing');
    setProcessing(true);
    setError(null);
    
    try {
      // Always downscale huge mobile camera photos before sending (prevents Vision API timeouts)
      const safeImageUrl = await downscaleIfLarge(imageDataUrl);
      // Extract base64 data
      const base64Data = safeImageUrl.split(',')[1];
      const mimeType = safeImageUrl.split(';')[0].split(':')[1];
      
      // Build request body with course context if available
      const requestBody = {
        image_base64: base64Data,
        mime_type: mimeType
      };
      
      if (selectedCourse && selectedCourse.id) {
        requestBody.course_id = selectedCourse.id;
      }
      
      const response = await fetch(`${API}/social/scan-scorecard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ..._getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(60000), // 60 second timeout
      });
      
      if (!response.ok) {
        // Read body as text first so we can surface non-JSON errors (gateway/HTML 500 pages)
        const rawText = await response.text().catch(() => '');
        let detail = '';
        try {
          const parsed = JSON.parse(rawText);
          detail = parsed.detail || parsed.error || '';
        } catch { /* not JSON */ }
        // Auto-retry once on transient failures (500/502/503/504/429) — most resolve on second try
        const isTransient = response.status >= 500 || response.status === 429;
        if (isTransient && attempt === 1) {
          console.warn(`Scan attempt 1 failed (${response.status}). Retrying in 3s…`);
          await new Promise(r => setTimeout(r, 3000));
          return processImage(imageDataUrl, 2);
        }
        // Map common HTTP status codes to actionable messages
        if (response.status === 402) {
          throw new Error('AI scan budget exceeded. Top up at Profile → Universal Key → Add Balance.');
        }
        if (response.status === 401 || response.status === 403) {
          throw new Error('Please sign in again — your session expired.');
        }
        if (response.status === 429) {
          throw new Error('Too many scans right now. Wait a minute and try again.');
        }
        if (response.status === 413) {
          throw new Error('Image too large — try Gallery and pick a smaller photo.');
        }
        if (response.status >= 500) {
          console.error('Scan 500 response body:', rawText.slice(0, 500));
          throw new Error(detail ? `Scan service error: ${detail.slice(0, 200)}` : 'Scan service is busy. Try a clearer photo with the scorecard flat and well-lit.');
        }
        throw new Error(detail || `Scan failed (HTTP ${response.status})`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        // Surface the real reason — raw_response has the GPT-4o output that failed to parse
        const rawHint = result.raw_response ? ` — AI said: "${String(result.raw_response).slice(0, 120)}"` : '';
        throw new Error((result.error || 'Failed to scan scorecard') + rawHint);
      }
      
      // Auto-match player names to members
      const dataWithMatches = { ...result.data };
      if (dataWithMatches.players) {
        dataWithMatches.players = dataWithMatches.players.map(player => {
          const match = matchNameToMember(player.name);
          if (match) {
            return {
              ...player,
              name: match.name, // Use exact member name
              member_id: match.user_id,
              name_confidence: 'high' // Boost confidence since we matched
            };
          }
          return player;
        });
      }
      
      setScannedData(dataWithMatches);
      setMode('confirm');
      
    } catch (err) {
      console.error('Scan error:', err);
      let msg = 'Failed to scan scorecard. Please try again.';
      if (err.name === 'TimeoutError' || err.name === 'AbortError') {
        msg = 'Scan timed out — try using Gallery or HD Cam for a clearer image';
      } else if (err.message?.includes('unsupported image')) {
        msg = 'Image format not supported — try using HD Cam or Gallery';
      } else if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        msg = 'Network error — check your connection and try again';
      } else if (err.message) {
        msg = err.message;
      }
      setError(msg);
      setMode('camera');
    } finally {
      setProcessing(false);
    }
  };

  // Retry scan
  const handleRetry = () => {
    setCapturedImage(null);
    setScannedData(null);
    setError(null);
    setMode('camera');
    startCamera();
  };

  // Update player data
  const updatePlayer = (index, field, value) => {
    setScannedData(prev => {
      const updated = { ...prev };
      updated.players = [...updated.players];
      updated.players[index] = { ...updated.players[index], [field]: value };
      return updated;
    });
  };

  // Update player score and recalculate totals
  const updateScore = (playerIndex, hole, value) => {
    setScannedData(prev => {
      const updated = { ...prev };
      updated.players = [...updated.players];
      
      const player = { ...updated.players[playerIndex] };
      const scores = { ...player.scores };
      
      // Update the specific hole score
      scores[hole] = value === '' ? null : parseInt(value) || null;
      
      // Recalculate OUT (front 9: holes 1-9)
      let outTotal = 0;
      let outCount = 0;
      for (let h = 1; h <= 9; h++) {
        const s = scores[String(h)];
        if (s !== null && s !== undefined) {
          outTotal += s;
          outCount++;
        }
      }
      scores.out = outCount > 0 ? outTotal : null;
      
      // Recalculate IN (back 9: holes 10-18)
      let inTotal = 0;
      let inCount = 0;
      for (let h = 10; h <= 18; h++) {
        const s = scores[String(h)];
        if (s !== null && s !== undefined) {
          inTotal += s;
          inCount++;
        }
      }
      scores.in = inCount > 0 ? inTotal : null;
      
      // Recalculate TOTAL
      scores.total = (scores.out || 0) + (scores.in || 0) || null;
      
      player.scores = scores;
      updated.players[playerIndex] = player;
      
      return updated;
    });
  };

  // Add player (blank — user types name, autocomplete links to Bundle member)
  const addPlayer = () => {
    const parsMap = {};
    if (selectedCourse?.holes?.length === 18) {
      selectedCourse.holes.forEach((h, idx) => { parsMap[idx + 1] = h.par || null; });
    }
    setScannedData(prev => ({
      ...prev,
      players: [...prev.players, { name: 'New Player', scores: {}, pars: parsMap }]
    }));
  };

  // Add a Bundle member directly (guarantees member_id is set → passes peer-verification)
  const addBundleMember = (member) => {
    if (!member?.user_id) return;
    const parsMap = {};
    if (selectedCourse?.holes?.length === 18) {
      selectedCourse.holes.forEach((h, idx) => { parsMap[idx + 1] = h.par || null; });
    }
    setScannedData(prev => ({
      ...prev,
      players: [
        ...(prev?.players || []),
        {
          name: member.name,
          member_id: member.user_id,
          scores: {},
          pars: parsMap,
          name_confidence: 'high',
          score_confidence: {},
        },
      ],
    }));
  };

  // Remove player
  const removePlayer = (index) => {
    setScannedData(prev => ({
      ...prev,
      players: prev.players.filter((_, i) => i !== index)
    }));
  };

  // Add current scan to groups and scan another
  const scanAnotherCard = () => {
    if (scannedData?.players?.length > 0) {
      setAllScannedGroups(prev => [...prev, scannedData]);
    }
    setScannedData(null);
    setCapturedImage(null);
    setCurrentGroupIndex(prev => prev + 1);
    setError(null);
    // Directly open HD camera — skip the camera preview
    setTimeout(() => {
      if (nativeCameraRef.current) {
        nativeCameraRef.current.value = ''; // Reset so same file can be selected
        nativeCameraRef.current.click();
      }
    }, 300);
  };

  // Remove a group
  const removeGroup = (groupIndex) => {
    setAllScannedGroups(prev => prev.filter((_, i) => i !== groupIndex));
  };

  // Calculate skins across ALL players from ALL groups (using useMemo for reactivity)
  const { skins, skinCounts, playerSkinMap } = React.useMemo(() => {
    // Combine all players
    const combinedPlayers = [];
    allScannedGroups.forEach((group, groupIdx) => {
      group.players?.forEach((player, playerIdx) => {
        combinedPlayers.push({
          ...player,
          globalId: `g${groupIdx}_p${playerIdx}`,
          groupIndex: groupIdx,
          playerIndex: playerIdx
        });
      });
    });
    // Add current scannedData if present AND not already merged into groups.
    // Without the includes() guard we double-count every player on Confirm →
    // every hole ties, no skin winners, and skins_by_hole ends up empty.
    if (scannedData?.players && !allScannedGroups.includes(scannedData)) {
      scannedData.players.forEach((player, playerIdx) => {
        combinedPlayers.push({
          ...player,
          globalId: `g${allScannedGroups.length}_p${playerIdx}`,
          groupIndex: allScannedGroups.length,
          playerIndex: playerIdx
        });
      });
    }

    if (combinedPlayers.length < 2) {
      return { skins: {}, skinCounts: {}, playerSkinMap: {} };
    }

    // Safety: cap skins calculation at 200 players to prevent browser freeze
    const playersForSkins = combinedPlayers.slice(0, 200);
    
    const skinsResult = {};
    const skinCountsResult = {};
    
    playersForSkins.forEach((p) => {
      skinCountsResult[p.globalId] = 0;
    });
    
    for (let hole = 1; hole <= 18; hole++) {
      const holeKey = String(hole);
      const scores = playersForSkins.map((player) => ({
        globalId: player.globalId,
        name: player.name,
        score: player.scores?.[holeKey]
      })).filter(s => s.score !== null && s.score !== undefined);
      
      if (scores.length === 0) {
        skinsResult[holeKey] = null;
        continue;
      }
      
      const minScore = Math.min(...scores.map(s => s.score));
      const winners = scores.filter(s => s.score === minScore);
      
      if (winners.length === 1) {
        skinsResult[holeKey] = winners[0].globalId;
        skinCountsResult[winners[0].globalId]++;
      } else {
        skinsResult[holeKey] = null;
      }
    }
    
    const playerMap = {};
    playersForSkins.forEach(p => {
      playerMap[p.globalId] = { name: p.name, skins: skinCountsResult[p.globalId] || 0, scores: p.scores };
    });
    
    return { skins: skinsResult, skinCounts: skinCountsResult, playerSkinMap: playerMap };
  }, [scannedData, allScannedGroups]);

  // Get total player count across all groups
  const totalPlayerCount = allPlayers.length;

  // Confirm and proceed
  const handleConfirm = () => {
    // Add current scan to groups before proceeding
    if (scannedData?.players?.length > 0 && !allScannedGroups.includes(scannedData)) {
      setAllScannedGroups(prev => [...prev, scannedData]);
    }
    setMode('action');
  };

  // Handle action selection
  const handleAction = async (action) => {
    // Combine all groups for output
    const allGroups = [...allScannedGroups];
    if (scannedData?.players && !allGroups.some(g => g === scannedData)) {
      allGroups.push(scannedData);
    }
    
    // Flatten all players with group info
    const combinedPlayers = [];
    allGroups.forEach((group, groupIdx) => {
      group.players?.forEach((player, playerIdx) => {
        const globalId = `g${groupIdx}_p${playerIdx}`;
        combinedPlayers.push({
          ...player,
          groupIndex: groupIdx,
          groupName: `Group ${groupIdx + 1}`,
          skins: skinCounts[globalId] || 0
        });
      });
    });

    // Handle "Import to Stats" directly
    if (action === 'stats') {
      try {
        const res = await fetch(`${API}/social/import-stats`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ..._getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({
            course_name: scannedData?.course_name || allGroups[0]?.course_name || '',
            date: scannedData?.date || allGroups[0]?.date || '',
            players: combinedPlayers.map(p => ({
              name: p.name,
              scores: p.scores || {},
              member_id: p.member_id || null,
              skins: p.skins || 0,
              pars: p.pars || {},
            })),
            course_rating: selectedCourse?.course_rating || null,
            slope_rating: selectedCourse?.slope_rating || null,
          }),
        });
        if (res.ok) {
          const result = await res.json();
          const memberCount = result.players?.filter(p => p.is_member).length || 0;
          const handicapUpdates = result.players?.filter(p => p.new_handicap !== null).length || 0;
          toast.success(`Imported ${result.total_imported} players! ${handicapUpdates > 0 ? `${handicapUpdates} handicap(s) updated.` : ''}`);
        } else {
          toast.error('Failed to import stats');
        }
      } catch (err) {
        toast.error('Network error importing stats');
      }
      if (onClose) onClose();
      return;
    }

    if (onScanComplete) {
      onScanComplete({
        action,
        data: {
          course_name: scannedData?.course_name || allGroups[0]?.course_name,
          date: scannedData?.date || allGroups[0]?.date,
          players: combinedPlayers,
          groups: allGroups,
          totalGroups: allGroups.length,
          overall_confidence: scannedData?.overall_confidence,
          recap_summary: (typeof window !== 'undefined' && window.__recapSummary) ? window.__recapSummary : null,
        },
        image: capturedImage,
        skins: {
          winners: skins,
          counts: skinCounts,
          playerMap: playerSkinMap,
          totalSkins: Object.values(skins).filter(s => s !== null).length,
          tiedHoles: 18 - Object.values(skins).filter(s => s !== null).length
        }
      });
    }
    if (onClose) onClose();
  };

  // Start camera on mount
  React.useEffect(() => {
    if (mode === 'camera') {
      startCamera();
    }
    return () => stopCamera();
  }, [mode, startCamera, stopCamera]);

  // Build an empty scannedData structure for manual entry — mirrors the scanner's output shape
  // so PostRoundModal / handleAction / skins calc all work identically.
  const buildEmptyScannedData = useCallback((course) => {
    const parsMap = {};
    if (course?.holes && course.holes.length === 18) {
      course.holes.forEach((h, idx) => { parsMap[idx + 1] = h.par || null; });
    }
    return {
      course_name: course?.name || '',
      date: new Date().toISOString().split('T')[0],
      overall_confidence: 'high',
      players: [
        {
          name: currentUser?.name || '',
          member_id: currentUser?.user_id || null,
          scores: {},
          pars: parsMap,
          name_confidence: 'high',
          score_confidence: {},
        },
      ],
    };
  }, [currentUser]);

  // Handle course selection — always proceed to camera immediately, check for hole data in the background
  const handleCourseSelect = (course) => {
    // Course IDs from the vendor are now alphanumeric strings (e.g. "crjqvm6g"),
    // not numeric — don't coerce with parseInt, it silently produces NaN.
    const rawId = course.api_id || (course.id && String(course.id).replace(/^api_/, ''));
    const normalized = rawId ? { ...course, id: rawId, api_id: rawId } : course;
    setSelectedCourse(normalized);
    // Manual mode: skip camera, jump straight into the editable grid
    if (isManualMode) {
      setScannedData(buildEmptyScannedData(normalized));
      setMode('confirm');
    } else {
      setMode('camera');
    }
    // Background: attach hole data if available (non-blocking, never gates the camera)
    if (normalized.id) {
      fetch(`${API}/course-scorecards/${normalized.id}/holes?tee_name=White`, {
        credentials: 'include', headers: _getAuthHeaders()
      }).then(res => res.ok ? res.json() : null)
        .then(async data => {
          // If the primary ID has no hole data (e.g. a "recent_*" fallback ID), try
          // to resolve a real vendor course by name and refetch.
          if (!data || !Array.isArray(data.holes) || data.holes.length !== 18 || !data.holes.some(h => h.par)) {
            const nameForLookup = normalized.name || normalized.course_name || normalized.club_name;
            if (nameForLookup && !normalized.api_id) {
              try {
                const searchRes = await fetch(`${API}/social/courses/search?q=${encodeURIComponent(nameForLookup)}`, {
                  credentials: 'include', headers: _getAuthHeaders(),
                });
                if (searchRes.ok) {
                  const searchData = await searchRes.json();
                  const realCourse = (searchData.courses || []).find(c => c.api_id);
                  if (realCourse && realCourse.api_id) {
                    const holeRes = await fetch(`${API}/course-scorecards/${realCourse.api_id}/holes?tee_name=White`, {
                      credentials: 'include', headers: _getAuthHeaders(),
                    });
                    if (holeRes.ok) {
                      const holeData = await holeRes.json();
                      if (holeData?.holes && holeData.holes.length === 18 && holeData.holes.some(h => h.par)) {
                        setSelectedCourse(prev => ({ ...prev, id: realCourse.api_id, api_id: realCourse.api_id, holes: holeData.holes, hasHoleData: true }));
                        if (isManualMode) {
                          setScannedData(prev => {
                            if (!prev) return prev;
                            const parsMap = {};
                            holeData.holes.forEach((h, idx) => { parsMap[idx + 1] = h.par || null; });
                            const players = (prev.players || []).map((p, i) => i === 0 ? { ...p, pars: { ...parsMap, ...(p.pars || {}) } } : p);
                            return { ...prev, players };
                          });
                        }
                        return;
                      }
                    }
                  }
                }
              } catch (_e) { /* ignore, fall through to missing state */ }
            }
            setSelectedCourse(prev => ({ ...prev, hasHoleData: false }));
            return;
          }
          // Primary path — holes came back valid
          setSelectedCourse(prev => ({ ...prev, holes: data.holes, hasHoleData: true }));
          if (isManualMode) {
            setScannedData(prev => {
              if (!prev) return prev;
              const parsMap = {};
              data.holes.forEach((h, idx) => { parsMap[idx + 1] = h.par || null; });
              const players = (prev.players || []).map((p, i) => i === 0 ? { ...p, pars: { ...parsMap, ...(p.pars || {}) } } : p);
              return { ...prev, players };
            });
          }
        }).catch(() => {});
    }
  };

  // Skip course selection
  const handleSkipCourse = () => {
    setSelectedCourse(null);
    if (isManualMode) {
      setScannedData(buildEmptyScannedData(null));
      setMode('confirm');
    } else {
      setMode('camera');
    }
  };

  // Go back to course selection
  const handleBackToCourse = () => {
    stopCamera();
    setMode('course');
    setCapturedImage(null);
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80">
        <button 
          onClick={mode === 'camera' ? handleBackToCourse : onClose} 
          className="p-2 text-white hover:bg-white/10 rounded-full"
          data-testid="scanner-back-btn"
        >
          <X className="w-6 h-6" />
        </button>
        <h1 className="text-white font-heading font-bold">
          {mode === 'course' ? 'Select Course' : (isManualMode ? 'Enter Scorecard' : 'Scan Scorecard')}
        </h1>
        <div className="w-10" />
      </div>

      {/* Course Selection View */}
      {mode === 'course' && (
        <div className="flex-1 overflow-y-auto bg-gray-900 p-4">
          {/* Info Banner */}
          <div className="bg-bundle-green/20 border border-bundle-green/40 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-bundle-green flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-white">{isManualMode ? 'Select the course you played' : 'Improve Accuracy'}</p>
                <p className="text-sm text-gray-300 mt-1">
                  {isManualMode
                    ? "We'll prefill hole pars from the course so you only have to type in strokes."
                    : 'Select your course for better score recognition. The AI will use par values to validate scores.'}
                </p>
              </div>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={courseSearch}
              onChange={(e) => setCourseSearch(e.target.value)}
              placeholder="Search for your course..."
              className="w-full bg-white/10 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-bundle-green"
              data-testid="course-search-input"
            />
          </div>

          {/* Course List */}
          <div className="space-y-2 mb-6">
            {loadingCourses ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-bundle-green animate-spin" />
              </div>
            ) : courses.length > 0 ? (
              courses.map((course) => (
                <button
                  key={course.id}
                  onClick={() => handleCourseSelect(course)}
                  className="w-full flex items-center justify-between bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 transition-colors"
                  data-testid={`course-option-${course.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-bundle-green/20 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-bundle-green" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-white flex items-center gap-2">
                        {course.name}
                        {course.verified_by && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-bundle-green/30 border border-bundle-green/50 text-[10px] font-bold text-bundle-green-light"
                                title={`Course details verified by ${course.verified_by}`}>
                            <span className="w-1 h-1 rounded-full bg-bundle-green-light"></span>
                            Verified
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400">
                        Par {course.total_par}
                        {course.city && course.state && (
                          <span className="ml-2">{course.city}, {course.state}</span>
                        )}
                        {course.verified_by && (
                          <span className="ml-2 text-bundle-green/80">by {course.verified_by}</span>
                        )}
                        {course.is_recent && <span className="ml-2 text-bundle-gold">Recently played</span>}
                        {course.source === 'golfcourseapi' && !course.is_recent && !course.verified_by && (
                          <span className="ml-2 text-bundle-green/70">Golf Course API</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </button>
              ))
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>No courses found</p>
                <p className="text-sm mt-1">Try a different search or skip this step</p>
              </div>
            )}
          </div>

          {/* Skip Button */}
          <div className="sticky bottom-4">
            <button
              onClick={handleSkipCourse}
              className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white font-medium transition-colors"
              data-testid="skip-course-btn"
            >
              {isManualMode ? 'Skip - Enter Course Name Manually' : 'Skip - Scan Without Course Data'}
            </button>
          </div>
        </div>
      )}

      {/* Fill Course Details — shown when API has no hole data */}
      {mode === 'fill_course' && selectedCourse && (
        <FillCourseDetails
          course={selectedCourse}
          onBack={() => setMode(fillReturnMode || 'course')}
          onSave={(filledCourse) => {
            setSelectedCourse(filledCourse);
            setMode(fillReturnMode || 'camera');
          }}
        />
      )}

      {/* Hidden inputs — always available for "Scan Another Card" */}
      <input
        ref={nativeCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleNativeCapture}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={handleFileUpload}
        className="hidden"
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* Camera View — Native Camera First */}
      {mode === 'camera' && (
        <div className="flex-1 relative bg-black flex flex-col overflow-hidden" style={{ minHeight: 'calc(100vh - 60px)' }}>
          {/* In-app camera preview */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Selected course indicator */}
          {selectedCourse && (
            <div className="absolute top-4 left-4 right-4">
              <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-bundle-green" />
                <span className="text-white text-sm truncate">{selectedCourse.name}</span>
                <span className="text-gray-400 text-xs ml-auto">Par {selectedCourse.total_par}</span>
              </div>
              {selectedCourse.hasHoleData === false && (
                <button
                  type="button"
                  onClick={() => { setFillReturnMode('camera'); setMode('fill_course'); }}
                  className="mt-2 w-full text-[11px] bg-bundle-gold/20 hover:bg-bundle-gold/30 text-bundle-gold border border-bundle-gold/40 rounded-lg px-2 py-1.5 transition-colors"
                  data-testid="fill-course-details-btn"
                >
                  ⚠️ Course has no par/yardage data — tap to fill it in (optional)
                </button>
              )}
            </div>
          )}

          {/* Overlay frame */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[90%] aspect-[4/3] border-2 border-white/50 rounded-lg relative">
              <div className="absolute -top-8 left-0 right-0 text-center text-white text-sm">
                Align scorecard inside the frame
              </div>
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-bundle-green rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-bundle-green rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-bundle-green rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-bundle-green rounded-br-lg" />
            </div>
          </div>

          {error && (
            <div className="absolute top-20 left-4 right-4 bg-red-500/90 text-white p-4 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Camera Error</p>
                <p className="text-sm opacity-90">{error}</p>
              </div>
            </div>
          )}

          {/* Controls — HD Camera + Gallery */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
            <div className="text-center mb-4">
              <p className="text-emerald-400 text-xs font-medium">
                Tap to open camera and capture your scorecard
              </p>
            </div>

            <div className="flex items-center justify-center gap-8">
              {/* Gallery Upload */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-1.5 p-3 bg-white/15 hover:bg-white/25 rounded-xl text-white transition-colors"
                data-testid="upload-image-btn"
              >
                <Plus className="w-6 h-6" />
                <span className="text-[10px] font-medium">Gallery</span>
              </button>

              {/* HD Camera (PRIMARY) */}
              <button
                onClick={() => nativeCameraRef.current?.click()}
                className="w-20 h-20 bg-white rounded-full flex items-center justify-center border-4 border-emerald-400 shadow-lg shadow-emerald-500/30 active:scale-95 transition-transform"
                data-testid="native-camera-btn"
              >
                <Camera className="w-8 h-8 text-emerald-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing View */}
      {mode === 'processing' && (
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          {capturedImage && (
            <img src={capturedImage} alt="Captured" className="max-w-full max-h-48 rounded-lg mb-8 opacity-50" />
          )}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <Loader2 className="w-12 h-12 text-bundle-green animate-spin" />
              <Zap className="w-5 h-5 text-bundle-gold absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-white font-medium">Analyzing scorecard...</p>
            <p className="text-gray-400 text-sm">Using AI to extract scores</p>
          </div>
        </div>
      )}

      {/* Confirmation View */}
      {mode === 'confirm' && scannedData && (
        <div className="flex-1 overflow-y-auto bg-gray-900 p-4">
          {/* Review Warning - show if needs_review flag or low confidence */}
          {(scannedData.needs_review || scannedData.overall_confidence === 'low') && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-400">Review Required</p>
                <p className="text-sm text-gray-300">Some scores are uncertain. Fields highlighted in orange need your attention.</p>
              </div>
            </div>
          )}

          {/* Standard Warning */}
          <div className="bg-bundle-gold/20 border border-bundle-gold/50 rounded-lg p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-bundle-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-bundle-gold">
                {isManualMode ? 'Enter your scorecard' : 'Please review before importing'}
              </p>
              <p className="text-sm text-gray-300">
                {isManualMode
                  ? 'Type strokes for each hole. Add other Bundle members you played with (name field auto-suggests).'
                  : 'Tap any field to edit. Make sure all scores are correct.'}
              </p>
            </div>
          </div>

          {/* Confidence indicator — hidden in manual entry mode */}
          {!isManualMode && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-gray-400 text-sm">Overall Confidence:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                scannedData.overall_confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                scannedData.overall_confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {scannedData.overall_confidence?.toUpperCase() || 'MEDIUM'}
              </span>
              {selectedCourse && (
                <span className="ml-auto text-xs text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {selectedCourse.name}
                </span>
              )}
            </div>
          )}
          {isManualMode && selectedCourse && (
            <div className="flex items-center gap-2 mb-4">
              <span className="ml-auto text-xs text-gray-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {selectedCourse.name}
              </span>
            </div>
          )}

          {/* Edit / Fill Course Details — always available from confirm view when a course is selected */}
          {selectedCourse && (
            <button
              type="button"
              onClick={() => { setFillReturnMode('confirm'); setMode('fill_course'); }}
              className={`w-full mb-4 flex items-center justify-between rounded-xl px-4 py-3 border transition-colors ${
                selectedCourse.hasHoleData === false
                  ? 'bg-bundle-gold/15 hover:bg-bundle-gold/25 border-bundle-gold/50 text-bundle-gold'
                  : 'bg-white/5 hover:bg-white/10 border-white/15 text-gray-200'
              }`}
              data-testid="edit-course-details-btn"
            >
              <div className="flex items-center gap-2">
                <Edit2 className="w-4 h-4" />
                <span className="text-sm font-semibold">
                  {selectedCourse.hasHoleData === false
                    ? 'Add course par & yardage'
                    : 'Edit course par / yardage'}
                </span>
              </div>
              <span className="text-[10px] opacity-80">
                {selectedCourse.hasHoleData === false ? 'Missing hole data' : 'Saved for this course'}
              </span>
            </button>
          )}

          {/* Multi-Group Summary */}
          {allScannedGroups.length > 0 && (
            <div className="bg-bundle-green/10 border border-bundle-green/30 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-bundle-green font-medium text-sm flex items-center gap-2">
                  <span>📋</span> {allScannedGroups.length + 1} Groups Scanned ({totalPlayerCount} players total)
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {allScannedGroups.map((group, idx) => (
                  <div key={idx} className="bg-white/10 rounded-lg px-3 py-1.5 flex items-center gap-2">
                    <span className="text-white text-xs font-medium">Group {idx + 1}</span>
                    <span className="text-gray-400 text-xs">({group.players?.length || 0} players)</span>
                    <button
                      onClick={() => removeGroup(idx)}
                      className="text-red-400 hover:text-red-300 ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <div className="bg-bundle-green/20 rounded-lg px-3 py-1.5 flex items-center gap-2 border border-bundle-green/50">
                  <span className="text-bundle-green text-xs font-medium">Group {allScannedGroups.length + 1}</span>
                  <span className="text-gray-400 text-xs">(current)</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Skins are calculated across ALL {totalPlayerCount} players
              </p>
            </div>
          )}

          {/* Warnings from OCR */}
          {scannedData.warnings && scannedData.warnings.length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4">
              <p className="text-xs text-orange-400 font-medium mb-1">Warnings:</p>
              <ul className="text-xs text-gray-400 space-y-1">
                {scannedData.warnings.map((warning, idx) => (
                  <li key={idx}>• {warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Course & Date */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Course</label>
              <input
                type="text"
                value={scannedData.course_name || selectedCourse?.name || ''}
                onChange={(e) => setScannedData(prev => ({ ...prev, course_name: e.target.value }))}
                placeholder="Enter course name"
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="course-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Date</label>
              <input
                type="date"
                value={scannedData.date || ''}
                onChange={(e) => setScannedData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="round-date-input"
              />
            </div>
          </div>

          {/* Confidence Legend */}
          <div className="flex items-center gap-4 mb-4 text-xs flex-wrap">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded border-2 border-green-500" />
              <span className="text-gray-400">High confidence</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded border-2 border-orange-500" />
              <span className="text-gray-400">Needs review</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-bundle-gold/40 border-2 border-bundle-gold" />
              <span className="text-gray-400">Won skin (lowest unique)</span>
            </div>
          </div>

          {/* Manual-mode: "I played in this round" toggle — lets user post rounds they didn't play in */}
          {isManualMode && currentUser?.user_id && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-4 flex items-center justify-between" data-testid="i-played-toggle-row">
              <div className="flex-1 min-w-0 pr-3">
                <p className="text-white text-sm font-medium">I played in this round</p>
                <p className="text-gray-400 text-xs mt-0.5">
                  Turn off if you&apos;re posting a round you observed but didn&apos;t play in. You still need to tag at least one Bundle member on the card.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={(scannedData.players || []).some(p => p.member_id === currentUser.user_id)}
                data-testid="toggle-i-played"
                onClick={() => {
                  setScannedData(prev => {
                    if (!prev) return prev;
                    const players = [...(prev.players || [])];
                    const alreadyIn = players.some(p => p.member_id === currentUser.user_id);
                    if (alreadyIn) {
                      // Remove poster
                      return { ...prev, players: players.filter(p => p.member_id !== currentUser.user_id) };
                    } else {
                      // Add poster as first player
                      const parsMap = {};
                      if (selectedCourse?.holes?.length === 18) {
                        selectedCourse.holes.forEach((h, idx) => { parsMap[idx + 1] = h.par || null; });
                      }
                      const posterRow = {
                        name: currentUser.name || '',
                        member_id: currentUser.user_id,
                        scores: {},
                        pars: parsMap,
                        name_confidence: 'high',
                        score_confidence: {},
                      };
                      return { ...prev, players: [posterRow, ...players] };
                    }
                  });
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  (scannedData.players || []).some(p => p.member_id === currentUser.user_id)
                    ? 'bg-bundle-green'
                    : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    (scannedData.players || []).some(p => p.member_id === currentUser.user_id) ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Players */}
          {scannedData.players?.map((player, pIndex) => {
            // Calculate globalId for this player (current group)
            const globalId = `g${allScannedGroups.length}_p${pIndex}`;
            const playerSkinCount = skinCounts[globalId] || 0;
            const matchedMember = player.member_id ? members.find(m => m.user_id === player.member_id) : null;
            
            return (
            <div key={pIndex} className="bg-white/5 rounded-xl p-4 mb-4" data-testid={`player-card-${pIndex}`}>
              <div className="flex items-center gap-3 mb-4">
                {/* Player Name with Member Autocomplete */}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={player.name}
                    onChange={(e) => {
                      updatePlayer(pIndex, 'name', e.target.value);
                      setMemberSearchQuery(e.target.value);
                      setActiveMemberDropdown(pIndex);
                    }}
                    onFocus={() => {
                      setMemberSearchQuery(player.name || '');
                      setActiveMemberDropdown(pIndex);
                    }}
                    onBlur={() => {
                      // Delay to allow click on dropdown
                      setTimeout(() => setActiveMemberDropdown(null), 200);
                    }}
                    placeholder="Player name"
                    className={`w-full bg-white/10 rounded-lg px-3 py-2 text-white font-medium border-2 ${
                      matchedMember ? 'border-bundle-green/70' : 
                      player.name_confidence === 'low' ? 'border-orange-500/70' : 'border-white/20'
                    }`}
                    data-testid={`player-name-${pIndex}`}
                  />
                  
                  {/* Matched Member Badge */}
                  {matchedMember && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <span className="bg-bundle-green/20 text-bundle-green text-xs px-2 py-0.5 rounded">
                        ✓ Member
                      </span>
                    </div>
                  )}
                  
                  {/* Member Dropdown */}
                  {activeMemberDropdown === pIndex && filteredMembers.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-gray-800 border border-white/20 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                      {filteredMembers.map((member) => (
                        <button
                          key={member.user_id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-white/10 flex items-center gap-2 border-b border-white/5 last:border-0"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            updatePlayer(pIndex, 'name', member.name);
                            updatePlayer(pIndex, 'member_id', member.user_id);
                            setActiveMemberDropdown(null);
                          }}
                        >
                          {member.picture ? (
                            <img src={member.picture} alt="" className="w-6 h-6 rounded-full" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-bundle-green/20 flex items-center justify-center text-bundle-green text-xs">
                              {member.name?.charAt(0) || '?'}
                            </div>
                          )}
                          <span className="text-white text-sm">{member.name}</span>
                          {member.handicap && (
                            <span className="text-gray-400 text-xs ml-auto">HCP: {member.handicap}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Skins count badge */}
                {playerSkinCount > 0 && (
                  <div className="bg-bundle-gold/20 border border-bundle-gold px-2 py-1 rounded-lg flex items-center gap-1">
                    <span className="text-bundle-gold text-xs font-bold">{playerSkinCount}</span>
                    <span className="text-bundle-gold text-xs">skin{playerSkinCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {player.name_confidence === 'low' && !matchedMember && (
                  <span className="text-orange-400 text-xs">Review name</span>
                )}
                <button
                  onClick={() => removePlayer(pIndex)}
                  className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                  data-testid={`remove-player-${pIndex}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              {/* Front 9 Scores */}
              <div className="grid grid-cols-10 gap-1 text-center text-xs mb-1">
                {[1,2,3,4,5,6,7,8,9,'OUT'].map(hole => (
                  <div key={hole} className="text-gray-500 font-medium">{hole}</div>
                ))}
              </div>
              {/* Par Row */}
              <div className="grid grid-cols-10 gap-1 mb-1">
                {[1,2,3,4,5,6,7,8,9].map(hole => {
                  const hasExplicitPar = player.pars && Object.prototype.hasOwnProperty.call(player.pars, hole);
                  const displayVal = hasExplicitPar
                    ? (player.pars[hole] == null ? '' : player.pars[hole])
                    : (selectedCourse?.holes?.[hole-1]?.par ?? '');
                  return (
                    <input key={`par-${hole}`} type="number" min="3" max="6" inputMode="numeric"
                      value={displayVal}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const val = raw === '' ? null : parseInt(raw);
                        setScannedData(prev => {
                          const updated = { ...prev };
                          updated.players = [...updated.players];
                          const p = { ...updated.players[pIndex] };
                          p.pars = { ...(p.pars || {}), [hole]: Number.isNaN(val) ? null : val };
                          updated.players[pIndex] = p;
                          return updated;
                        });
                      }}
                      onFocus={(e) => e.target.select()}
                      className="w-full rounded px-1 py-1 text-center text-[10px] bg-gray-700/50 border border-gray-600 text-gray-400"
                      title="Par"
                      data-testid={`inline-par-${hole}-player-${pIndex}`}
                    />
                  );
                })}
                <div className="text-gray-500 text-[10px] text-center py-1">PAR</div>
              </div>
              <div className="grid grid-cols-10 gap-1 mb-2">
                {[1,2,3,4,5,6,7,8,9].map(hole => {
                  const confidence = player.score_confidence?.[String(hole)] || 'medium';
                  const isLowConfidence = confidence === 'low';
                  const wonSkin = skins[String(hole)] === globalId;
                  return (
                    <input
                      key={hole}
                      type="number"
                      min="1"
                      max="15"
                      value={player.scores?.[hole] ?? ''}
                      onChange={(e) => updateScore(pIndex, String(hole), e.target.value)}
                      className={`w-full rounded px-1 py-1.5 text-center text-sm border-2 transition-colors ${
                        wonSkin
                          ? 'bg-bundle-gold/30 border-bundle-gold text-white font-bold'
                          : isLowConfidence 
                            ? 'border-orange-500/70 bg-orange-500/10 text-white' 
                            : 'bg-white/10 border-white/20 text-white focus:border-bundle-green'
                      }`}
                      title={wonSkin ? '🏆 Won skin!' : isLowConfidence ? 'Low confidence - please verify' : ''}
                      data-testid={`score-hole-${hole}-player-${pIndex}`}
                    />
                  );
                })}
                <div className="bg-bundle-green/20 rounded px-1 py-1.5 text-bundle-green text-center text-sm font-bold border-2 border-transparent">
                  {player.scores?.out || '-'}
                </div>
              </div>

              {/* Back 9 Scores */}
              <div className="grid grid-cols-10 gap-1 text-center text-xs mb-1 mt-3">
                {[10,11,12,13,14,15,16,17,18,'IN'].map(hole => (
                  <div key={hole} className="text-gray-500 font-medium">{hole}</div>
                ))}
              </div>
              {/* Par Row Back 9 */}
              <div className="grid grid-cols-10 gap-1 mb-1">
                {[10,11,12,13,14,15,16,17,18].map(hole => {
                  const hasExplicitPar = player.pars && Object.prototype.hasOwnProperty.call(player.pars, hole);
                  const displayVal = hasExplicitPar
                    ? (player.pars[hole] == null ? '' : player.pars[hole])
                    : (selectedCourse?.holes?.[hole-1]?.par ?? '');
                  return (
                    <input key={`par-${hole}`} type="number" min="3" max="6" inputMode="numeric"
                      value={displayVal}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const val = raw === '' ? null : parseInt(raw);
                        setScannedData(prev => {
                          const updated = { ...prev };
                          updated.players = [...updated.players];
                          const p = { ...updated.players[pIndex] };
                          p.pars = { ...(p.pars || {}), [hole]: Number.isNaN(val) ? null : val };
                          updated.players[pIndex] = p;
                          return updated;
                        });
                      }}
                      onFocus={(e) => e.target.select()}
                      className="w-full rounded px-1 py-1 text-center text-[10px] bg-gray-700/50 border border-gray-600 text-gray-400"
                      title="Par"
                      data-testid={`inline-par-${hole}-player-${pIndex}`}
                    />
                  );
                })}
                <div className="text-gray-500 text-[10px] text-center py-1">PAR</div>
              </div>
              <div className="grid grid-cols-10 gap-1 mb-2">
                {[10,11,12,13,14,15,16,17,18].map(hole => {
                  const confidence = player.score_confidence?.[String(hole)] || 'medium';
                  const isLowConfidence = confidence === 'low';
                  const wonSkin = skins[String(hole)] === globalId;
                  return (
                    <input
                      key={hole}
                      type="number"
                      min="1"
                      max="15"
                      value={player.scores?.[hole] ?? ''}
                      onChange={(e) => updateScore(pIndex, String(hole), e.target.value)}
                      className={`w-full rounded px-1 py-1.5 text-center text-sm border-2 transition-colors ${
                        wonSkin
                          ? 'bg-bundle-gold/30 border-bundle-gold text-white font-bold'
                          : isLowConfidence 
                            ? 'border-orange-500/70 bg-orange-500/10 text-white' 
                            : 'bg-white/10 border-white/20 text-white focus:border-bundle-green'
                      }`}
                      title={wonSkin ? '🏆 Won skin!' : isLowConfidence ? 'Low confidence - please verify' : ''}
                      data-testid={`score-hole-${hole}-player-${pIndex}`}
                    />
                  );
                })}
                <div className="bg-bundle-green/20 rounded px-1 py-1.5 text-bundle-green text-center text-sm font-bold border-2 border-transparent">
                  {player.scores?.in || '-'}
                </div>
              </div>

              {/* Total, Skins, and Per-Player Stats */}
              {(() => {
                // Calculate per-player stats
                let eagles = 0, birdies = 0, parsCount = 0, bogeys = 0, doubles = 0, holesPlayed = 0;
                for (let h = 1; h <= 18; h++) {
                  const score = player.scores?.[String(h)];
                  const par = player.pars?.[h] ?? (selectedCourse?.holes?.[h-1]?.par || 4);
                  if (score == null) continue;
                  holesPlayed++;
                  const diff = score - par;
                  if (diff <= -2) eagles++;
                  else if (diff === -1) birdies++;
                  else if (diff === 0) parsCount++;
                  else if (diff === 1) bogeys++;
                  else doubles++;
                }
                const scoreToPar = (player.scores?.total || 0) - [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18].reduce((sum, h) => {
                  if (player.scores?.[String(h)] == null) return sum;
                  return sum + (player.pars?.[h] ?? (selectedCourse?.holes?.[h-1]?.par || 4));
                }, 0);
                return (
                  <div className="mt-3 space-y-2">
                    <div className="flex justify-between items-center">
                      {playerSkinCount > 0 && (
                        <div className="bg-bundle-gold/10 border border-bundle-gold/30 rounded-lg px-3 py-2">
                          <span className="text-bundle-gold font-bold">{playerSkinCount} Skin{playerSkinCount !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                      <div className="bg-bundle-gold/20 rounded-lg px-4 py-2 ml-auto flex items-center gap-2">
                        <span className="text-gray-400 text-sm">Total:</span>
                        <span className="text-bundle-gold font-bold text-lg">{player.scores?.total || '-'}</span>
                        {scoreToPar !== 0 && holesPlayed > 0 && (
                          <span className={`text-xs font-bold ${scoreToPar < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                            ({scoreToPar > 0 ? '+' : ''}{scoreToPar})
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Stats Chips */}
                    {holesPlayed > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {eagles > 0 && <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-[10px] font-bold border border-yellow-500/30">{eagles} Eagle{eagles > 1 ? 's' : ''}</span>}
                        {birdies > 0 && <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold border border-red-500/30">{birdies} Birdie{birdies > 1 ? 's' : ''}</span>}
                        {parsCount > 0 && <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[10px] font-bold border border-green-500/30">{parsCount} Par{parsCount > 1 ? 's' : ''}</span>}
                        {bogeys > 0 && <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] font-bold border border-blue-500/30">{bogeys} Bogey{bogeys > 1 ? 's' : ''}</span>}
                        {doubles > 0 && <span className="px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 text-[10px] font-bold border border-gray-500/30">{doubles} Double+</span>}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
          })}

          {/* Skins Summary - All Players Across All Groups */}
          {totalPlayerCount >= 2 && Object.values(skinCounts).some(c => c > 0) && (
            <div className="bg-bundle-gold/10 border border-bundle-gold/30 rounded-xl p-4 mb-4">
              <h3 className="text-bundle-gold font-bold mb-3 flex items-center gap-2">
                <span className="text-lg">🏆</span> Skins Summary 
                {allScannedGroups.length > 0 && (
                  <span className="text-xs font-normal text-gray-400">
                    ({totalPlayerCount} players across {allScannedGroups.length + 1} groups)
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(playerSkinMap)
                  .sort((a, b) => b[1].skins - a[1].skins)
                  .map(([globalId, playerInfo]) => (
                  <div key={globalId} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2">
                    <span className="text-white text-sm truncate">{playerInfo.name}</span>
                    <span className={`font-bold ${playerInfo.skins > 0 ? 'text-bundle-gold' : 'text-gray-500'}`}>
                      {playerInfo.skins || 0}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {18 - Object.values(skins).filter(s => s !== null).length} hole{18 - Object.values(skins).filter(s => s !== null).length !== 1 ? 's' : ''} tied (no winner)
              </p>
            </div>
          )}

          {/* Group Summary — Low Gross, Low Net, CTP, Stats Leaders */}
          {(() => {
            // Guard against double-counting: after handleConfirm pushes scannedData
            // into allScannedGroups, it stays as the same object reference. Without
            // this includes() check we'd count every player twice, which nukes
            // skins, bundle_wins, and the whole recap summary.
            const scannedAlreadyMerged = scannedData && allScannedGroups.includes(scannedData);
            const allPlayers = [
              ...(allScannedGroups.flatMap((g, gi) => (g.players || []).map((p, pi) => ({ ...p, globalId: `g${gi}_p${pi}` })))),
              ...(!scannedAlreadyMerged && scannedData?.players
                ? scannedData.players.map((p, pi) => ({ ...p, globalId: `g${allScannedGroups.length}_p${pi}` }))
                : []),
            ];
            if (allPlayers.length < 1) return null;

            // Build par-by-hole using first player with pars or course data
            const parByHole = {};
            for (let h = 1; h <= 18; h++) {
              const cp = allPlayers.find(p => p.pars?.[h] != null)?.pars?.[h];
              parByHole[h] = cp ?? (selectedCourse?.holes?.[h-1]?.par ?? null);
            }
            const par3Holes = Object.entries(parByHole)
              .filter(([, par]) => par === 3)
              .map(([h]) => parseInt(h))
              .sort((a, b) => a - b);

            // Calculate stats for each player (incl. handicap lookup via member_id)
            const playerStats = allPlayers.map(p => {
              let eagles = 0, birdies = 0, pars = 0, bogeys = 0, doubles = 0, total = p.scores?.total || 0, holes = 0, totalPar = 0;
              let front9 = 0, back9 = 0, front9Holes = 0, back9Holes = 0;
              const holeScores = {};
              for (let h = 1; h <= 18; h++) {
                const score = p.scores?.[String(h)];
                const par = parByHole[h] ?? 4;
                if (score == null) continue;
                holes++;
                totalPar += par;
                holeScores[h] = score;
                if (h <= 9) { front9 += score; front9Holes++; }
                else { back9 += score; back9Holes++; }
                const diff = score - par;
                if (diff <= -2) eagles++;
                else if (diff === -1) birdies++;
                else if (diff === 0) pars++;
                else if (diff === 1) bogeys++;
                else doubles++;
              }
              // Resolve handicap from matched member (if any)
              const matched = p.member_id ? members.find(m => m.user_id === p.member_id) : null;
              const handicap = matched?.handicap != null ? Number(matched.handicap) : null;
              const netTotal = handicap != null ? total - handicap : null;
              return {
                name: p.name, globalId: p.globalId, memberId: p.member_id || null,
                total, holes, totalPar, scoreToPar: total - totalPar,
                eagles, birdies, pars, bogeys, doubles,
                skins: skinCounts[p.globalId] || 0,
                handicap, netTotal,
                front9: front9Holes === 9 ? front9 : (front9Holes > 0 ? front9 : null),
                back9: back9Holes === 9 ? back9 : (back9Holes > 0 ? back9 : null),
                front9Holes, back9Holes,
                holeScores,
              };
            }).filter(p => p.holes > 0);

            if (playerStats.length < 1) return null;

            // Find winners
            const lowGross = [...playerStats].sort((a, b) => a.total - b.total)[0];
            const bestToPar = [...playerStats].sort((a, b) => a.scoreToPar - b.scoreToPar)[0];
            const netEligible = playerStats.filter(p => p.netTotal != null);
            const lowNet = netEligible.length > 0 ? [...netEligible].sort((a, b) => a.netTotal - b.netTotal)[0] : null;
            const mostBirdies = [...playerStats].sort((a, b) => (b.birdies + b.eagles * 2) - (a.birdies + a.eagles * 2))[0];
            const mostSkins = [...playerStats].sort((a, b) => b.skins - a.skins)[0];

            // CTP list: hole -> assigned player info (if any)
            const ctpList = par3Holes.map(h => {
              const gid = ctpAssignments[h];
              const winner = gid ? playerStats.find(p => p.globalId === gid) : null;
              return { hole: h, winner_global_id: gid || null, winner_name: winner?.name || null };
            });
            const ctpAssignedCount = ctpList.filter(c => c.winner_name).length;

            const courseName = scannedData?.course_name || allScannedGroups[0]?.course_name || selectedCourse?.name || 'Round';
            const roundDate = scannedData?.date || allScannedGroups[0]?.date || new Date().toISOString().slice(0,10);

            const buildRecapText = () => {
              const fullBoard = [...playerStats].sort((a, b) => a.total - b.total);
              const lines = [
                `⛳ ${courseName} — ${roundDate}`,
                ``,
                `🏆 Low Gross: ${lowGross.name} (${lowGross.total}${bestToPar.scoreToPar > 0 ? ', +' + bestToPar.scoreToPar : bestToPar.scoreToPar === 0 ? '' : ', ' + bestToPar.scoreToPar})`,
              ];
              if (lowNet) {
                lines.push(`🎯 Low Net: ${lowNet.name} (${lowNet.netTotal}, HCP ${lowNet.handicap})`);
              }
              if (mostBirdies.birdies > 0 || mostBirdies.eagles > 0) {
                lines.push(`🐦 Most Birdies: ${mostBirdies.name} (${mostBirdies.birdies}${mostBirdies.eagles > 0 ? ' + ' + mostBirdies.eagles + '🦅' : ''})`);
              }
              if (mostSkins.skins > 0) {
                lines.push(`💰 Most Skins: ${mostSkins.name} (${mostSkins.skins})`);
              }
              if (ctpAssignedCount > 0) {
                lines.push(``, `📍 CTP (Closest to Pin):`);
                ctpList.forEach(c => {
                  if (c.winner_name) lines.push(`  Hole ${c.hole}: ${c.winner_name}`);
                });
              }
              lines.push(``, `LEADERBOARD:`);
              fullBoard.forEach((p, i) => {
                const rank = i + 1;
                const toPar = p.scoreToPar > 0 ? `+${p.scoreToPar}` : p.scoreToPar === 0 ? 'E' : `${p.scoreToPar}`;
                const netStr = p.netTotal != null ? ` · Net ${p.netTotal}` : '';
                lines.push(`${rank}. ${p.name} — ${p.total} (${toPar})${netStr}`);
              });
              lines.push(``, `Posted from The Bundle Golf Club`);
              return lines.join('\n');
            };

            const handleShareRecap = async () => {
              const text = buildRecapText();
              // Save the recap to the backend so we get a public shareable link
              // that includes the FULL recap (not just text). Falls back to
              // text-only share if the API is unreachable.
              let shareUrl = null;
              try {
                const res = await fetch(`${API}/recaps`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json', ..._getAuthHeaders() },
                  body: JSON.stringify(window.__recapSummary || {
                    course_name: courseName, date: roundDate,
                    low_gross: { name: lowGross.name, total: lowGross.total, score_to_par: bestToPar.scoreToPar },
                    leaderboard: [...playerStats].sort((a,b) => a.total - b.total).map(p => ({
                      name: p.name, total: p.total, score_to_par: p.scoreToPar,
                      handicap: p.handicap, net_total: p.netTotal,
                    })),
                  }),
                });
                if (res.ok) {
                  const data = await res.json();
                  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
                  // Prefer og_url so link-preview crawlers (iMessage / WhatsApp /
                  // Slack) can render an inline card via server-side OG tags.
                  // The endpoint meta-refreshes clickers to /recap/:shareId.
                  shareUrl = origin + (data.og_url || data.url || `/recap/${data.share_id}`);
                }
              } catch (_e) { /* stay text-only */ }

              const shareText = shareUrl
                ? `${text}\n\n👉 Full recap: ${shareUrl}`
                : text;

              // Try sharing an image of the Group Summary card first so the
              // scorecard renders inline in iMessage / WhatsApp instead of just
              // a link. Falls through to text/URL share on any failure.
              try {
                const node = typeof document !== 'undefined' && document.querySelector('[data-testid="group-summary"]');
                if (node && navigator.canShare) {
                  const { default: html2canvas } = await import('html2canvas');
                  const canvas = await html2canvas(node, { backgroundColor: '#0f2417', scale: 2, useCORS: true });
                  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
                  if (blob) {
                    const file = new File([blob], `bundle-recap-${(courseName || 'round').replace(/\s+/g, '-')}.png`, { type: 'image/png' });
                    if (navigator.canShare({ files: [file] })) {
                      try {
                        await navigator.share({ files: [file], title: `${courseName} Recap`, text: shareText, url: shareUrl || undefined });
                        return;
                      } catch (_e) { /* fall through */ }
                    }
                  }
                }
              } catch (_e) { /* fall through */ }

              if (navigator.share) {
                try {
                  await navigator.share({ title: `${courseName} Recap`, text: shareText, url: shareUrl || undefined });
                  return;
                } catch (_e) { /* fall through */ }
              }
              try {
                await navigator.clipboard.writeText(shareText);
                toast.success(shareUrl ? 'Recap link copied — paste anywhere!' : 'Recap copied to clipboard');
              } catch (_e) { toast.error('Could not copy recap'); }
            };

            // Save recap data to window so it gets submitted with the post
            if (typeof window !== 'undefined') {
              const nameByGlobalId = Object.fromEntries(playerStats.map(p => [p.globalId, p.name]));
              const skinsByHole = [];
              for (let h = 1; h <= 18; h++) {
                const winnerGid = skins[String(h)];
                if (!winnerGid) continue;
                const winner = playerStats.find(p => p.globalId === winnerGid);
                if (!winner) continue;
                skinsByHole.push({
                  hole: h,
                  winner_name: nameByGlobalId[winnerGid] || winner.name,
                  score: winner.holeScores?.[h] ?? null,
                  par: parByHole[h] ?? null,
                });
              }
              const f9Eligible = playerStats.filter(p => p.front9 != null && p.front9Holes === 9);
              const b9Eligible = playerStats.filter(p => p.back9 != null && p.back9Holes === 9);
              const bundleWins = {
                front9: f9Eligible.length > 0
                  ? (() => { const w = [...f9Eligible].sort((a,b)=>a.front9-b.front9)[0]; return { name: w.name, total: w.front9 }; })()
                  : null,
                back9: b9Eligible.length > 0
                  ? (() => { const w = [...b9Eligible].sort((a,b)=>a.back9-b.back9)[0]; return { name: w.name, total: w.back9 }; })()
                  : null,
                overall: lowGross ? { name: lowGross.name, total: lowGross.total } : null,
              };
              window.__recapSummary = {
                course_name: courseName,
                date: roundDate,
                // low_score kept for backwards compatibility with existing feed cards
                low_score: { name: lowGross.name, total: lowGross.total, score_to_par: bestToPar.scoreToPar },
                low_gross: { name: lowGross.name, total: lowGross.total, score_to_par: bestToPar.scoreToPar },
                low_net: lowNet ? { name: lowNet.name, net_total: lowNet.netTotal, handicap: lowNet.handicap, gross: lowNet.total } : null,
                most_birdies: (mostBirdies.birdies > 0 || mostBirdies.eagles > 0) ? { name: mostBirdies.name, birdies: mostBirdies.birdies, eagles: mostBirdies.eagles } : null,
                most_skins: mostSkins.skins > 0 ? { name: mostSkins.name, skins: mostSkins.skins } : null,
                par3_holes: par3Holes,
                ctp: ctpList.filter(c => c.winner_name).map(c => ({ hole: c.hole, name: c.winner_name })),
                pars: parByHole,
                skins_by_hole: skinsByHole,
                bundle_wins: bundleWins,
                leaderboard: [...playerStats].sort((a, b) => a.total - b.total).map(p => ({
                  name: p.name, total: p.total, score_to_par: p.scoreToPar,
                  eagles: p.eagles, birdies: p.birdies, pars: p.pars, skins: p.skins,
                  handicap: p.handicap, net_total: p.netTotal,
                  front9: p.front9, back9: p.back9,
                  scores: p.holeScores,
                })),
              };
            }

            return (
              <>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-4" data-testid="group-summary">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-emerald-400 font-bold flex items-center gap-2">
                    <span className="text-lg">📊</span> Group Summary
                  </h3>
                  <button
                    type="button"
                    onClick={handleShareRecap}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-xs font-bold transition-colors"
                    data-testid="share-recap-btn"
                    title="Share recap — copy to clipboard or forward"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share Recap
                  </button>
                </div>
                <div className="space-y-2">
                  {/* Low Gross */}
                  <div className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2.5" data-testid="recap-low-gross">
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-400">🏆</span>
                      <span className="text-gray-400 text-xs">Low Gross</span>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-bold text-sm">{lowGross.name}</span>
                      <span className="text-emerald-400 font-bold text-sm ml-2">{lowGross.total}</span>
                      <span className="text-gray-500 text-xs ml-1">({bestToPar.scoreToPar > 0 ? '+' : ''}{bestToPar.scoreToPar})</span>
                    </div>
                  </div>
                  {/* Low Net */}
                  {lowNet && (
                    <div className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2.5" data-testid="recap-low-net">
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-400">🎯</span>
                        <span className="text-gray-400 text-xs">Low Net</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white font-bold text-sm">{lowNet.name}</span>
                        <span className="text-cyan-400 font-bold text-sm ml-2">{lowNet.netTotal}</span>
                        <span className="text-gray-500 text-xs ml-1">(HCP {lowNet.handicap})</span>
                      </div>
                    </div>
                  )}
                  {/* Most Birdies */}
                  {(mostBirdies.birdies > 0 || mostBirdies.eagles > 0) && (
                    <div className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-red-400">🐦</span>
                        <span className="text-gray-400 text-xs">Most Birdies</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white font-bold text-sm">{mostBirdies.name}</span>
                        <span className="text-red-400 font-bold text-sm ml-2">{mostBirdies.birdies}{mostBirdies.eagles > 0 ? ` + ${mostBirdies.eagles}🦅` : ''}</span>
                      </div>
                    </div>
                  )}
                  {/* Most Skins */}
                  {mostSkins.skins > 0 && (
                    <div className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-bundle-gold">💰</span>
                        <span className="text-gray-400 text-xs">Most Skins</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white font-bold text-sm">{mostSkins.name}</span>
                        <span className="text-bundle-gold font-bold text-sm ml-2">{mostSkins.skins} skins</span>
                      </div>
                    </div>
                  )}
                  {/* CTP Winners (collapsed list) */}
                  {ctpAssignedCount > 0 && (
                    <div className="bg-black/20 rounded-lg px-3 py-2.5" data-testid="recap-ctp-summary">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-pink-400">📍</span>
                        <span className="text-gray-400 text-xs">CTP Winners</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {ctpList.filter(c => c.winner_name).map(c => (
                          <span key={c.hole} className="text-[10px] bg-pink-500/20 border border-pink-500/30 text-pink-300 font-semibold px-2 py-0.5 rounded-full">
                            H{c.hole} · {c.winner_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Full Leaderboard */}
                <div className="mt-3 border-t border-white/10 pt-3">
                  <p className="text-[10px] text-gray-500 uppercase font-bold mb-2">Full Leaderboard</p>
                  <div className="space-y-1">
                    {[...playerStats].sort((a, b) => a.total - b.total).map((p, i) => (
                      <div key={p.globalId} className="flex items-center gap-2 text-xs py-1">
                        <span className={`w-5 text-center font-bold ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-600' : 'text-gray-500'}`}>{i + 1}</span>
                        <span className="text-white flex-1 truncate">{p.name}</span>
                        <span className="text-gray-500 text-[10px]">{p.eagles > 0 ? `${p.eagles}🦅 ` : ''}{p.birdies > 0 ? `${p.birdies}🐦 ` : ''}{p.pars > 0 ? `${p.pars}P ` : ''}</span>
                        <span className="text-white font-bold w-8 text-right">{p.total}</span>
                        {p.netTotal != null && (
                          <span className="text-cyan-400 w-10 text-right text-[10px] font-bold" title={`Net (HCP ${p.handicap})`}>
                            N{p.netTotal}
                          </span>
                        )}
                        <span className={`w-10 text-right text-[10px] font-bold ${p.scoreToPar < 0 ? 'text-red-400' : p.scoreToPar === 0 ? 'text-gray-400' : 'text-gray-500'}`}>
                          {p.scoreToPar > 0 ? '+' : ''}{p.scoreToPar}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTP Assignment Card — only when we detected par-3s */}
              {par3Holes.length > 0 && playerStats.length >= 1 && (
                <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-4 mb-4" data-testid="ctp-assignment-card">
                  <h3 className="text-pink-300 font-bold flex items-center gap-2 mb-1">
                    <span className="text-lg">📍</span> Closest to Pin (Par-3s)
                  </h3>
                  <p className="text-[11px] text-pink-200/80 mb-3">
                    Tap to mark who got CTP on each par-3 hole.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {par3Holes.map(h => (
                      <div key={h} className="bg-black/20 rounded-lg p-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-pink-300 text-[11px] font-bold">Hole {h}</span>
                          <span className="text-gray-500 text-[10px]">Par 3</span>
                        </div>
                        <select
                          value={ctpAssignments[h] || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCtpAssignments(prev => {
                              const next = { ...prev };
                              if (!val) delete next[h];
                              else next[h] = val;
                              return next;
                            });
                          }}
                          className="w-full text-xs bg-white/5 border border-white/20 rounded px-2 py-1 text-white"
                          data-testid={`ctp-select-${h}`}
                        >
                          <option value="" className="bg-gray-800">— no winner —</option>
                          {playerStats.map(p => (
                            <option key={p.globalId} value={p.globalId} className="bg-gray-800">
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
            );
          })()}

          {/* Add player button */}
          <button
            onClick={addPlayer}
            className="w-full py-3 border-2 border-dashed border-white/20 rounded-xl text-gray-400 hover:border-bundle-green hover:text-bundle-green transition-colors flex items-center justify-center gap-2"
            data-testid="add-player-btn"
          >
            <Plus className="w-5 h-5" />
            Add Player
          </button>

          {/* Manual mode: Add Bundle Member picker — guarantees member_id is set so peer-verification passes */}
          {isManualMode && (
            <div className="mt-2">
              {!showMemberPicker ? (
                <button
                  onClick={() => { setShowMemberPicker(true); setMemberPickerQuery(''); }}
                  className="w-full py-3 border-2 border-dashed border-bundle-green/50 rounded-xl text-bundle-green hover:border-bundle-green hover:bg-bundle-green/10 transition-colors flex items-center justify-center gap-2 font-medium"
                  data-testid="add-bundle-member-btn"
                >
                  <UserPlus className="w-5 h-5" />
                  Add Bundle Member
                </button>
              ) : (
                <div className="bg-white/5 border border-bundle-green/30 rounded-xl p-3" data-testid="member-picker">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      autoFocus
                      type="text"
                      value={memberPickerQuery}
                      onChange={(e) => setMemberPickerQuery(e.target.value)}
                      placeholder="Search Bundle members by name..."
                      className="flex-1 bg-transparent text-white placeholder:text-gray-500 focus:outline-none text-sm"
                      data-testid="member-picker-input"
                    />
                    <button
                      onClick={() => setShowMemberPicker(false)}
                      className="text-gray-400 hover:text-white"
                      data-testid="member-picker-close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-white/5">
                    {(members
                      .filter(m => !memberPickerQuery || m.name?.toLowerCase().includes(memberPickerQuery.toLowerCase()))
                      .filter(m => !(scannedData?.players || []).some(p => p.member_id === m.user_id))
                      .slice(0, 20)
                    ).map((m) => (
                      <button
                        key={m.user_id}
                        type="button"
                        onClick={() => {
                          addBundleMember(m);
                          setMemberPickerQuery('');
                        }}
                        className="w-full flex items-center gap-2 p-2 hover:bg-white/5 rounded-lg text-left"
                        data-testid={`member-picker-option-${m.user_id}`}
                      >
                        {m.picture ? (
                          <img src={m.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-bundle-green/30 flex items-center justify-center text-bundle-green text-xs font-bold">
                            {m.name?.charAt(0) || '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{m.name}</p>
                          {m.handicap != null && (
                            <p className="text-gray-500 text-[10px]">HCP: {m.handicap}</p>
                          )}
                        </div>
                        <Plus className="w-4 h-4 text-bundle-green" />
                      </button>
                    ))}
                    {members.length === 0 && (
                      <p className="text-gray-500 text-xs py-3 text-center">Loading members...</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Scan Another Card button — hidden in manual entry mode */}
          {!isManualMode && (
            <button
              onClick={scanAnotherCard}
              className="w-full py-4 mt-2 bg-white border-2 border-bundle-green rounded-xl text-bundle-green hover:bg-bundle-green hover:text-white transition-colors flex items-center justify-center gap-2 font-bold shadow-md"
              data-testid="scan-another-btn"
            >
              <Camera className="w-5 h-5" />
              Scan Another Scorecard (Add More Players)
            </button>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 mt-6 pb-8">
            {!isManualMode && (
              <Button
                variant="outline"
                onClick={handleRetry}
                className="flex-1 border-white/20 text-white"
                data-testid="rescan-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Rescan
              </Button>
            )}
            <Button
              onClick={handleConfirm}
              className="flex-1 bg-bundle-green hover:bg-bundle-green/90 text-white"
              data-testid="confirm-scores-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              {isManualMode ? 'Continue' : 'Confirm'}
            </Button>
          </div>
        </div>
      )}

      {/* Action Selection View — simplified single-button flow */}
      {mode === 'action' && (
        <SimplifiedActionView
          onPost={() => handleAction('post')}
          onTournament={() => handleAction('tournament')}
          onBackToEdit={() => setMode('confirm')}
          courseName={scannedData?.course_name || allScannedGroups[0]?.course_name || ''}
        />
      )}
    </div>
  );
}

function SimplifiedActionView({ onPost, onTournament, onBackToEdit, courseName }) {
  const [activeTournaments, setActiveTournaments] = React.useState([]);
  const [loadingTourns, setLoadingTourns] = React.useState(true);

  React.useEffect(() => {
    const fetch_active = async () => {
      try {
        const res = await fetch(`${API}/tournaments?status=active`, { credentials: 'include', headers: _getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setActiveTournaments(Array.isArray(data) ? data : []);
        }
      } catch {}
      setLoadingTourns(false);
    };
    fetch_active();
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gray-900">
      <h2 className="text-white font-heading text-2xl font-bold mb-2">Post your round?</h2>
      <p className="text-gray-400 text-sm mb-8 text-center max-w-sm">
        We'll share it to the feed and update the leaderboard, handicap, and skins for every player on the card.
      </p>

      <div className="w-full max-w-sm space-y-3">
        <button
          onClick={onPost}
          className="w-full p-5 bg-gradient-to-r from-bundle-green to-emerald-600 hover:from-bundle-green-dark hover:to-emerald-700 rounded-xl text-left transition-all shadow-lg shadow-emerald-500/20"
          data-testid="action-post-round"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">⛳</span>
            </div>
            <div className="flex-1">
              <p className="font-bold text-white text-lg">Post Round</p>
              <p className="text-sm text-emerald-50/90">Feed + leaderboard + handicap</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/70" />
          </div>
        </button>

        {!loadingTourns && activeTournaments.length > 0 && (
          <button
            onClick={onTournament}
            className="w-full p-4 bg-bundle-gold/15 border border-bundle-gold/40 hover:bg-bundle-gold/25 rounded-xl text-left transition-colors"
            data-testid="action-add-to-tournament"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-bundle-gold/30 rounded-full flex items-center justify-center">
                <span className="text-xl">🏆</span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-white">Also add to {activeTournaments[0].name}</p>
                <p className="text-xs text-bundle-gold/80">{activeTournaments.length > 1 ? `+${activeTournaments.length - 1} other active` : 'Active tournament'}</p>
              </div>
            </div>
          </button>
        )}
      </div>

      <button
        onClick={onBackToEdit}
        className="mt-8 text-gray-400 hover:text-white text-sm"
        data-testid="back-to-edit-btn"
      >
        ← Go back and edit
      </button>
    </div>
  );
}


function FillCourseDetails({ course, onBack, onSave }) {
  // Default smart par-72 layout (10 par-4s, 4 par-3s, 4 par-5s)
  const DEFAULT_PARS = [4, 4, 5, 3, 4, 4, 5, 3, 4, 4, 4, 5, 3, 4, 4, 5, 3, 4];
  // If the course already has hole data, pre-populate so users can edit instead of reset
  const existingHoles = Array.isArray(course?.holes) && course.holes.length === 18 ? course.holes : null;
  const [holes, setHoles] = React.useState(
    Array.from({ length: 18 }, (_, i) => ({
      hole: i + 1,
      par: existingHoles?.[i]?.par ?? DEFAULT_PARS[i],
      yardage: existingHoles?.[i]?.yardage ?? 0,
    }))
  );
  const [teeName, setTeeName] = React.useState('White');
  const [saving, setSaving] = React.useState(false);

  const updatePar = (idx, val) => {
    const par = parseInt(val) || 4;
    if (par < 3 || par > 6) return;
    const next = [...holes];
    next[idx] = { ...next[idx], par };
    setHoles(next);
  };

  const updateYardage = (idx, val) => {
    const yardage = parseInt(val) || 0;
    if (yardage < 0 || yardage > 800) return;
    const next = [...holes];
    next[idx] = { ...next[idx], yardage };
    setHoles(next);
  };

  const totalPar = holes.reduce((s, h) => s + h.par, 0);
  const totalYards = holes.reduce((s, h) => s + (h.yardage || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const tee_box = {
        tee_name: teeName,
        gender: 'male',
        total_par: totalPar,
        total_yardage: totalYards,
        holes: holes,
      };
      // course.id may be "api_20069" — strip prefix to get the integer API id the backend expects
      const numericId = course.api_id || (course.id && String(course.id).replace(/^api_/, ''));
      if (!numericId || isNaN(parseInt(numericId))) {
        toast.error('Course has an invalid id — try re-selecting the course.');
        setSaving(false);
        return;
      }
      const res = await fetch(`${API}/course-scorecards/${parseInt(numericId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ..._getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          club_name: course.name || course.club_name || 'Unknown Course',
          course_name: course.name || course.course_name || 'Main Course',
          tee_boxes: [tee_box],
        }),
      });
      if (res.ok) {
        toast.success('Course details saved! Future scans at this course will use your data.');
        onSave({ ...course, id: parseInt(numericId), api_id: parseInt(numericId), holes, total_par: totalPar });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to save course details (${res.status})`);
      }
    } catch (e) {
      toast.error('Network error: ' + (e.message || 'unknown'));
    }
    setSaving(false);
  };

  const handleSkip = () => {
    onSave({ ...course, holes });
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-900 overflow-y-auto">
      <div className="p-6 max-w-2xl mx-auto w-full">
        <div className="bg-bundle-gold/15 border border-bundle-gold/40 rounded-xl p-4 mb-5">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-bundle-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-white">
                {existingHoles ? 'Edit course layout' : 'First time at this course'}
              </p>
              <p className="text-sm text-gray-300 mt-1">
                {existingHoles
                  ? <>Adjust par or yardage for <span className="font-semibold">{course.name}</span>. Your changes save for every future scan at this course.</>
                  : <>We don't have hole-by-hole data for <span className="font-semibold">{course.name}</span>. Fill it in once and every future scan here will use it automatically.</>}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm text-gray-300 font-medium">Tee box:</label>
          <select
            value={teeName}
            onChange={(e) => setTeeName(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-bundle-green"
            data-testid="fill-tee-name-select"
          >
            <option className="bg-gray-900">White</option>
            <option className="bg-gray-900">Blue</option>
            <option className="bg-gray-900">Gold</option>
            <option className="bg-gray-900">Red</option>
            <option className="bg-gray-900">Black</option>
          </select>
          <span className="ml-auto text-xs text-gray-400">Total: <span className="text-white font-bold">{totalPar}</span> par · <span className="text-white font-bold">{totalYards || '—'}</span> yds</span>
        </div>

        <div className="space-y-2 mb-6">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-gray-500 font-bold px-2">
            <div className="col-span-2">Hole</div>
            <div className="col-span-4">Par</div>
            <div className="col-span-6">Yardage <span className="text-gray-600 normal-case">(optional)</span></div>
          </div>
          {holes.map((h, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white/5 rounded-lg p-2" data-testid={`fill-hole-${idx + 1}`}>
              <div className="col-span-2 text-white font-bold">{h.hole}</div>
              <div className="col-span-4">
                <select
                  value={h.par}
                  onChange={(e) => updatePar(idx, e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:border-bundle-green"
                  data-testid={`fill-par-${idx + 1}`}
                >
                  {[3, 4, 5, 6].map(p => <option key={p} value={p} className="bg-gray-900">Par {p}</option>)}
                </select>
              </div>
              <div className="col-span-6">
                <input
                  type="number"
                  min={0} max={800}
                  value={h.yardage || ''}
                  onChange={(e) => updateYardage(idx, e.target.value)}
                  placeholder="—"
                  className="w-full bg-white/10 border border-white/20 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:border-bundle-green"
                  data-testid={`fill-yardage-${idx + 1}`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2 sticky bottom-4 bg-gray-900 pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-bundle-green hover:bg-bundle-green-dark text-white rounded-xl font-bold transition-colors disabled:opacity-50"
            data-testid="fill-course-save-btn"
          >
            {saving ? 'Saving...' : (existingHoles ? 'Save changes' : 'Save & Continue to Scan')}
          </button>
          <button
            onClick={existingHoles ? onBack : handleSkip}
            className="w-full py-2 text-gray-400 hover:text-white text-sm transition-colors"
            data-testid="fill-course-skip-btn"
          >
            {existingHoles ? 'Cancel — keep saved data' : 'Skip — use defaults for this scan only'}
          </button>
          {!existingHoles && (
            <button
              onClick={onBack}
              className="w-full py-2 text-gray-500 hover:text-white text-xs transition-colors"
              data-testid="fill-course-back-btn"
            >
              ← Choose different course
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
